import React, { useEffect, useState } from 'react';
import { formatItemAmount, importFromProcore, listFromProcore, sendToProcore } from '../procoreActions';
import {
  asList,
  getJson,
  integrationError,
  integrationKind,
  integrationLabel,
  isErpIntegration,
  isProjectToolIntegration,
  isSyncableIntegration,
  sendKindFor,
} from '../helpers';

function invoiceGross(invoice) {
  return (
    invoice.subtotal ||
    (Number(invoice.total_amount || 0) + Number(invoice.retention_held || 0))
  );
}

function erpInvoiceNeedsSend(invoice, mapping, integration) {
  if (!mapping) return true;
  if (integrationKind(integration) !== 'qbo') return false;
  const meta = mapping.external_metadata || {};
  const held = Number(invoice.retention_held || invoice.retainage_held || 0);
  const released = Number(invoice.retention_released || 0);
  if (held > 0 && !meta.retainage_qbo_journal_entry_id) return true;
  if (released > 0 && !meta.retention_release_qbo_journal_entry_id) return true;
  return false;
}

function invoiceTitle(invoice) {
  return invoice.invoice_number || invoice.vuid || 'AP Invoice';
}

function invoiceSubtitle(invoice) {
  const vendor = invoice.vendor?.vendor_name || invoice.vendor?.name || invoice.vendor_name;
  return [vendor, formatItemAmount(invoiceGross(invoice))].filter(Boolean).join(' · ');
}

function comboAllowed(integration, direction, recordType) {
  if (isProjectToolIntegration(integration) && direction === 'receive' && recordType === 'invoice') return true;
  if (isProjectToolIntegration(integration) && direction === 'send' && (recordType === 'direct-cost' || recordType === 'invoice')) {
    return true;
  }
  if (isErpIntegration(integration) && direction === 'send' && recordType === 'invoice' && sendKindFor(integration, 'ap_invoice')) {
    return true;
  }
  return false;
}

export default function InvoiceSyncModal({ projectVuid, onClose, onComplete }) {
  const [integrations, setIntegrations] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoaded, setInvoicesLoaded] = useState(false);
  const [directCostVuids, setDirectCostVuids] = useState(new Set());
  const [erpInvoiceMappings, setErpInvoiceMappings] = useState({});
  const [integrationVuid, setIntegrationVuid] = useState('');
  const [direction, setDirection] = useState('');
  const [recordType, setRecordType] = useState('');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState('init');
  const [flash, setFlash] = useState(null);

  const integration = integrations.find((row) => row.vuid === integrationVuid) || integrations[0];

  const resetChoice = () => {
    setDirection('');
    setRecordType('');
    setItems([]);
    setSelected([]);
    setFlash(null);
  };

  useEffect(() => {
    let cancelled = false;
    setBusy('init');
    Promise.all([
      getJson('/api/integrations'),
      getJson('/api/external-system-ids', { object_type: 'direct_cost', project_vuid: projectVuid }).catch(() => []),
      getJson('/api/external-system-ids', { object_type: 'ap_invoice' }).catch(() => []),
    ])
      .then(([integrationRows, dcRows, apRows]) => {
        if (cancelled) return;
        const nextIntegrations = asList(integrationRows).filter((row) => {
          if (String(row.status || '').toLowerCase() !== 'active') return false;
          return isSyncableIntegration(row);
        });
        setIntegrations(nextIntegrations);
        setIntegrationVuid(nextIntegrations[0]?.vuid || '');
        setDirectCostVuids(new Set(asList(dcRows).map((row) => row.object_vuid).filter(Boolean)));
        const nextErpMappings = {};
        asList(apRows).forEach((row) => {
          if (isErpIntegration(row.integration || row) && row.object_vuid) {
            nextErpMappings[row.object_vuid] = row;
          }
        });
        setErpInvoiceMappings(nextErpMappings);
      })
      .catch((err) => {
        if (!cancelled) setFlash({ type: 'err', text: integrationError(err) });
      })
      .finally(() => {
        if (!cancelled) setBusy('');
      });
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  useEffect(() => {
    resetChoice();
  }, [integrationVuid]);

  const loadItems = async (nextDirection, nextType) => {
    if (!integration || !nextDirection || !nextType) return;
    setBusy('load');
    setFlash(null);
    try {
      if (!comboAllowed(integration, nextDirection, nextType)) {
        setItems([]);
        setSelected([]);
        return;
      }
      let localInvoices = invoices;
      if (nextDirection === 'send' && !invoicesLoaded) {
        const invoiceRows = asList(await getJson('/api/ap-invoices').catch(() => []));
        localInvoices = invoiceRows.filter((row) => row.project_vuid === projectVuid);
        setInvoices(localInvoices);
        setInvoicesLoaded(true);
      }
      if (isProjectToolIntegration(integration) && nextDirection === 'receive' && nextType === 'invoice') {
        const result = await listFromProcore('ap-invoices', integration.vuid, projectVuid);
        const next = (result.items || [])
          .filter((row) => !row.alreadyImported)
          .map((row) => ({
            id: String(row.id),
            title: row.title,
            subtitle: [row.subtitle, formatItemAmount(row.amount)].filter(Boolean).join(' · '),
            importKind: 'ap-invoices',
            listItem: row,
          }));
        setItems(next);
        setSelected(next.map((row) => row.id));
        if (result.warnings?.length) setFlash({ type: 'err', text: result.warnings.slice(0, 4).join('\n') });
      } else if (isProjectToolIntegration(integration) && nextDirection === 'send') {
        const alreadySent = nextType === 'direct-cost' || nextType === 'invoice' ? directCostVuids : new Set();
        const next = localInvoices
          .filter((invoice) => !alreadySent.has(invoice.vuid))
          .map((invoice) => ({
            id: invoice.vuid,
            title: invoiceTitle(invoice),
            subtitle: invoiceSubtitle(invoice),
            sendKind: 'direct-cost',
            payload: {
              source_type: 'ap_invoice',
              source_vuid: invoice.vuid,
              ...(nextType === 'invoice' ? { direct_cost_type: 'invoice' } : {}),
            },
          }));
        setItems(next);
        setSelected(next.map((row) => row.id));
      } else if (isErpIntegration(integration) && nextDirection === 'send' && nextType === 'invoice') {
        const next = localInvoices
          .filter((invoice) => erpInvoiceNeedsSend(invoice, erpInvoiceMappings[invoice.vuid], integration))
          .map((invoice) => ({
            id: invoice.vuid,
            title: invoiceTitle(invoice),
            subtitle: invoiceSubtitle(invoice),
            sendKind: sendKindFor(integration, 'ap_invoice', 'ap-invoice'),
            payload: { ap_invoice_vuid: invoice.vuid },
          }));
        setItems(next);
        setSelected(next.map((row) => row.id));
      } else {
        setItems([]);
        setSelected([]);
      }
    } catch (err) {
      setItems([]);
      setSelected([]);
      setFlash({ type: 'err', text: integrationError(err) });
    } finally {
      setBusy('');
    }
  };

  const pickDirection = (nextDirection) => {
    setDirection(nextDirection);
    if (recordType) loadItems(nextDirection, recordType);
    else {
      setItems([]);
      setSelected([]);
      setFlash(null);
    }
  };

  const pickType = (nextType) => {
    setRecordType(nextType);
    if (direction) loadItems(direction, nextType);
  };

  const toggleItem = (id) => {
    setSelected((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  };

  const runSync = async () => {
    if (!integration || !direction || !recordType) return;
    const chosen = items.filter((row) => selected.includes(row.id));
    if (!chosen.length) {
      setFlash({ type: 'err', text: 'Select at least one record.' });
      return;
    }
    setBusy('sync');
    setFlash(null);
    const errors = [];
    let sent = 0;
    try {
      if (direction === 'receive') {
        const result = await importFromProcore('ap-invoices', integration.vuid, projectVuid, chosen.map((row) => row.listItem));
        sent += chosen.length;
        (result.errors || []).forEach((item) => errors.push(item));
      } else {
        for (const row of chosen) {
          try {
            const data = await sendToProcore(integration.vuid, row.sendKind, row.payload);
            sent += 1;
            if (data?.retainage_error) {
              errors.push(`${row.title}: Retainage journal entry: ${data.retainage_error}`);
            }
          } catch (err) {
            errors.push(`${row.title}: ${integrationError(err)}`);
          }
        }
      }
      const extra = errors.length ? `\n${errors.slice(0, 6).join('\n')}` : '';
      if (!sent && errors.length) {
        setFlash({ type: 'err', text: errors.slice(0, 6).join('\n') });
        return;
      }
      if (errors.length) {
        setFlash({ type: 'err', text: `Synced ${sent} item${sent === 1 ? '' : 's'}.${extra}` });
        return;
      }
      onComplete();
    } catch (err) {
      setFlash({ type: 'err', text: integrationError(err) });
    } finally {
      setBusy('');
    }
  };

  const sendInvoiceHint =
    isErpIntegration(integration)
      ? `Push this project’s AP invoices to ${integrationLabel(integration)} as bills.`
      : isProjectToolIntegration(integration)
        ? `Push this AP invoice to ${integrationLabel(integration)} Direct Costs as type Invoice.`
        : 'Send is only available for a project tool or ERP.';
  const sendDirectHint =
    isProjectToolIntegration(integration)
      ? `Push this AP invoice to ${integrationLabel(integration)} Direct Costs (subcontractor invoice when it is tied to a commitment).`
      : 'Direct Cost send is only available for the project tool.';
  const receiveInvoiceHint =
    isProjectToolIntegration(integration)
      ? `Pull unsynced approved subcontractor invoices (requisitions) from ${integrationLabel(integration)}.`
      : 'Receive invoices is only available for the project tool.';
  const receiveDirectHint = 'Direct costs are sent from Claret to Procore. There is no retrieve.';

  return (
    <div className="pc-modal-backdrop" onClick={() => busy !== 'load' && busy !== 'sync' && onClose()}>
      <div className="pc-modal" onClick={(event) => event.stopPropagation()}>
        <div className="pc-modal-head">
          <h2>Sync invoices</h2>
          <button type="button" className="pc-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="pc-modal-body">
          <p className="pc-muted" style={{ marginTop: 0 }}>
            Choose an integration, then send or receive invoices or direct costs.
          </p>
          {busy === 'init' ? <div className="pc-empty">Loading integrations…</div> : null}
          {busy !== 'init' && integrations.length === 0 ? (
            <p className="pc-flash pc-flash-err">No active integrations found.</p>
          ) : null}
          {busy !== 'init' && integrations.length > 0 ? (
            <>
              <div className="pc-sync-options">
                {integrations.map((row) => (
                  <button
                    key={row.vuid}
                    type="button"
                    className={`pc-choice${integrationVuid === row.vuid ? ' is-selected' : ''}`}
                    onClick={() => {
                      setIntegrationVuid(row.vuid);
                      resetChoice();
                    }}
                  >
                    <strong>{row.integration_name}</strong>
                    <span>{integrationLabel(row)}</span>
                  </button>
                ))}
              </div>
              {integration ? (
                <>
                  <div className="pc-sync-options">
                    <button
                      type="button"
                      className={`pc-choice${direction === 'send' ? ' is-selected' : ''}`}
                      onClick={() => pickDirection('send')}
                    >
                      <strong>Send</strong>
                      <span>
                        {isErpIntegration(integration)
                          ? `Push Claret AP invoices to ${integrationLabel(integration)}.`
                          : `Push Claret AP invoices to ${integrationLabel(integration)}.`}
                      </span>
                    </button>
                    <button
                      type="button"
                      className={`pc-choice${direction === 'receive' ? ' is-selected' : ''}`}
                      disabled={!isProjectToolIntegration(integration)}
                      onClick={() => pickDirection('receive')}
                    >
                      <strong>Receive</strong>
                      <span>
                        {isProjectToolIntegration(integration)
                          ? `Pull unsynced subcontractor invoices from ${integrationLabel(integration)}.`
                          : 'Receive is only available for the project tool.'}
                      </span>
                    </button>
                  </div>
                  {direction ? (
                    <div className="pc-sync-options">
                      <button
                        type="button"
                        className={`pc-choice${recordType === 'invoice' ? ' is-selected' : ''}`}
                        disabled={!comboAllowed(integration, direction, 'invoice')}
                        onClick={() => pickType('invoice')}
                      >
                        <strong>Invoice</strong>
                        <span>
                          {direction === 'receive' ? receiveInvoiceHint : sendInvoiceHint}
                        </span>
                      </button>
                      <button
                        type="button"
                        className={`pc-choice${recordType === 'direct-cost' ? ' is-selected' : ''}`}
                        disabled={!comboAllowed(integration, direction, 'direct-cost')}
                        onClick={() => pickType('direct-cost')}
                      >
                        <strong>Direct Cost</strong>
                        <span>
                          {direction === 'receive' ? receiveDirectHint : sendDirectHint}
                        </span>
                      </button>
                    </div>
                  ) : null}
                </>
              ) : null}
            </>
          ) : null}
          {busy === 'load' ? <div className="pc-empty">Loading unsynced records…</div> : null}
          {direction && recordType && busy !== 'load' && busy !== 'init' ? (
            items.length === 0 ? (
              <div className="pc-empty">
                {comboAllowed(integration, direction, recordType)
                  ? 'No unsynced records for this integration.'
                  : 'This combination is not available for the selected integration.'}
              </div>
            ) : (
              <>
                <div className="pc-import-toolbar">
                  <button
                    type="button"
                    className="pc-btn pc-btn-secondary pc-btn-sm"
                    onClick={() => setSelected(items.map((row) => row.id))}
                  >
                    Select all
                  </button>
                  <button type="button" className="pc-btn pc-btn-secondary pc-btn-sm" onClick={() => setSelected([])}>
                    Clear
                  </button>
                  <span className="pc-muted">{selected.length} selected</span>
                </div>
                <div className="pc-import-list">
                  {items.map((row) => (
                    <label key={row.id} className="pc-import-row">
                      <input
                        type="checkbox"
                        checked={selected.includes(row.id)}
                        onChange={() => toggleItem(row.id)}
                      />
                      <span>
                        <strong>{row.title}</strong>
                        <span className="pc-meta">{row.subtitle}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </>
            )
          ) : null}
          {flash ? (
            <div className={`pc-flash ${flash.type === 'ok' ? 'pc-flash-ok' : 'pc-flash-err'}`}>{flash.text}</div>
          ) : null}
        </div>
        <div className="pc-modal-actions">
          <button type="button" className="pc-btn pc-btn-secondary" onClick={onClose} disabled={busy === 'load' || busy === 'sync'}>
            Cancel
          </button>
          <button
            type="button"
            className="pc-btn"
            onClick={runSync}
            disabled={
              busy === 'load' ||
              busy === 'sync' ||
              busy === 'init' ||
              !direction ||
              !recordType ||
              !selected.length
            }
          >
            {busy === 'sync' ? 'Syncing…' : direction === 'receive' ? `Receive ${selected.length}` : `Send ${selected.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}
